import { lazy, Suspense } from 'react'
import type { RouteObject } from 'react-router-dom'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import { PageLoader } from '../components/common/PageLoader'
import { RootLayout } from '../layouts/RootLayout'
import { AuthLayout } from '../layouts/AuthLayout'
import { DashboardLayout } from '../layouts/DashboardLayout'
import { AuthGuard } from '../components/guards/AuthGuard'
import { RoleGuard } from '../components/guards/RoleGuard'
import { SuperAdminGuard } from '../features/ferpa/components/SuperAdminGuard'
import { SchoolHubTabRedirect } from './redirects'

// NOTE: Routes bypass the accounts/schools barrel modules (pages.ts) for lazy imports.
// Every `import('.../pages')` resolves to the same module, so Vite would collapse all
// pages into a single chunk — the login page would download admin-only pages too.
// Targeting per-page files here gives each route its own chunk. Feature code should
// still use the barrels per CLAUDE.md.
const LoginPage = lazy(() => import('../features/user-management/pages/LoginPage'))
const ForgotPasswordPage = lazy(
  () => import('../features/user-management/pages/ForgotPasswordPage'),
)
const ResetPasswordPage = lazy(() => import('../features/user-management/pages/ResetPasswordPage'))
const AcceptInvitationPage = lazy(
  () => import('../features/user-management/pages/auth/AcceptInvitationPage'),
)
const RegisterParentPage = lazy(
  () => import('../features/user-management/pages/auth/RegisterParentPage'),
)
const AcceptFamilyInvitePage = lazy(
  () => import('../features/user-management/pages/auth/AcceptFamilyInvitePage'),
)
const ConfirmEmailChangePage = lazy(
  () => import('../features/user-management/pages/auth/ConfirmEmailChangePage'),
)
const ParentEmailVerificationPage = lazy(
  () => import('../features/user-management/pages/auth/ParentEmailVerificationPage'),
)
const OAuthCallbackPage = lazy(
  () => import('../features/user-management/pages/auth/OAuthCallbackPage'),
)
const UsersListPage = lazy(() => import('../features/user-management/pages/list/UsersListPage'))
const UserDetailPage = lazy(() => import('../features/user-management/pages/detail/UserDetailPage'))
const InvitationsListPage = lazy(
  () => import('../features/user-management/pages/list/InvitationsListPage'),
)
const LoginHistoryPage = lazy(
  () => import('../features/user-management/pages/list/LoginHistoryPage'),
)
const ProfilePage = lazy(() => import('../features/user-management/pages/detail/ProfilePage'))
const ProfileEditPage = lazy(
  () => import('../features/user-management/pages/create-edit/ProfileEditPage'),
)
const UserManagementPage = lazy(
  () => import('../features/user-management/pages/UserManagementPage'),
)
const DashboardPage = lazy(() => import('../features/dashboard/pages/DashboardPage'))
const SystemDashboard = lazy(() => import('../features/dashboard/pages/SystemDashboard'))
const SchoolDashboard = lazy(() => import('../features/dashboard/pages/SchoolDashboard'))
const TeacherDashboard = lazy(() => import('../features/dashboard/pages/TeacherDashboard'))
const ParentDashboard = lazy(() => import('../features/dashboard/pages/ParentDashboard'))
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'))
const UnauthorizedPage = lazy(() => import('../pages/UnauthorizedPage'))
const SchoolsListPage = lazy(() => import('../features/school-management/pages/SchoolsListPage'))
const SchoolCreateWizardPage = lazy(
  () => import('../features/school-management/pages/create/SchoolCreateWizardPage'),
)
const SchoolSetupWizardPage = lazy(
  () => import('../features/school-management/pages/create/SchoolSetupWizardPage'),
)
const AcademicYearWizardPage = lazy(
  () => import('../features/school-management/pages/create/AcademicYearWizardPage'),
)
const RolloverWizardPage = lazy(
  () => import('../features/school-management/pages/create/RolloverWizardPage'),
)
const SchoolDetailPage = lazy(
  () => import('../features/school-management/pages/detail/SchoolDetailPage'),
)
const AdminRegistrationDetailPage = lazy(
  () => import('../features/registration/pages/AdminRegistrationDetailPage'),
)
const RegistrationFormCreatePage = lazy(
  () => import('../features/registration/pages/RegistrationFormCreatePage'),
)
const RegistrationFormEditPage = lazy(
  () => import('../features/registration/pages/RegistrationFormEditPage'),
)
const RegistrationManagementDashboardPage = lazy(
  () => import('../features/registration/pages/RegistrationManagementDashboardPage'),
)
const WithdrawalRequestsPage = lazy(
  () => import('../features/registration/pages/WithdrawalRequestsPage'),
)
const ReturningStudentsWizardPage = lazy(
  () => import('../features/registration/pages/ReturningStudentsWizardPage'),
)
const ReturningStudentsTrackingPage = lazy(
  () => import('../features/registration/pages/ReturningStudentsTrackingPage'),
)
const ParentReturningPage = lazy(() => import('../features/registration/pages/ParentReturningPage'))
const ClassDetailPage = lazy(() => import('../features/academics/pages/ClassDetailPage'))
const ClassesEntryPage = lazy(() => import('../features/academics/pages/ClassesEntryPage'))
const SchoolReportCardsPage = lazy(
  () => import('../features/academics/pages/SchoolReportCardsPage'),
)
const ParentRegistrationPage = lazy(
  () => import('../features/registration/pages/ParentRegistrationPage'),
)
const RegistrationInviteLandingPage = lazy(
  () => import('../features/registration/pages/RegistrationInviteLandingPage'),
)
const ParentRegistrationFormsPage = lazy(
  () => import('../features/registration/pages/ParentRegistrationFormsPage'),
)
const ParentPrivacyPage = lazy(() => import('../features/ferpa/parent/pages/ParentPrivacyPage'))
const ParentPrivacyHubPage = lazy(
  () => import('../features/ferpa/parent/pages/ParentPrivacyHubPage'),
)
const ReportCardPrintPage = lazy(() => import('../features/academics/pages/ReportCardPrintPage'))
const ParentChildDetailPage = lazy(
  () => import('../features/registration/pages/ParentChildDetailPage'),
)
const ParentPaymentsPage = lazy(() => import('../features/registration/pages/ParentPaymentsPage'))
const PaymentSuccessPage = lazy(() => import('../features/registration/pages/PaymentSuccessPage'))
const PaymentCancelPage = lazy(() => import('../features/registration/pages/PaymentCancelPage'))
const RegistrationPlanDetailPage = lazy(
  () => import('../features/registration/pages/RegistrationPlanDetailPage'),
)
const PaymentReceiptPage = lazy(() => import('../features/receipts/pages/PaymentReceiptPage'))
const FamilyStatementPage = lazy(() => import('../features/receipts/pages/FamilyStatementPage'))
const StudentsListPage = lazy(() => import('../features/students/pages/StudentsListPage'))
const StudentDetailPage = lazy(() => import('../features/students/pages/StudentDetailPage'))
const DocumentReviewQueuePage = lazy(
  () => import('../features/students/pages/DocumentReviewQueuePage'),
)
const PaymentFeedPage = lazy(() => import('../features/payments-admin/pages/PaymentFeedPage'))
const CustomerLedgerPage = lazy(() => import('../features/payments-admin/pages/CustomerLedgerPage'))
const CustomerDetailPage = lazy(() => import('../features/payments-admin/pages/CustomerDetailPage'))
const FollowUpQueuePage = lazy(() => import('../features/payments-admin/pages/FollowUpQueuePage'))
const PrivacyAuditPage = lazy(() => import('../features/ferpa/pages/PrivacyAuditPage'))
const PlatformStudentTimelinePage = lazy(
  () => import('../features/ferpa/pages/PlatformStudentTimelinePage'),
)
const ActorActivityPage = lazy(() => import('../features/ferpa/pages/ActorActivityPage'))
const SchoolPrivacyPage = lazy(() => import('../features/ferpa/pages/SchoolPrivacyPage'))
const SchoolStudentTimelinePage = lazy(
  () => import('../features/ferpa/pages/SchoolStudentTimelinePage'),
)
const RecordsRequestDetailPage = lazy(
  () => import('../features/ferpa/pages/RecordsRequestDetailPage'),
)
const RecordDisclosurePage = lazy(() => import('../features/ferpa/pages/RecordDisclosurePage'))

type AnyComponent = React.LazyExoticComponent<React.ComponentType<object>>
const LazyPage = (Component: AnyComponent) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
)

function RegistrationTabRedirect({ sub }: { sub: 'forms' | 'submissions' }) {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/schools/${id}?tab=registration&sub=${sub}`} replace />
}

function FormDetailToEditRedirect() {
  const { id, formId } = useParams<{ id: string; formId: string }>()
  return <Navigate to={`/schools/${id}/registration/${formId}/edit`} replace />
}

function TeacherClassRedirect() {
  const { classId } = useParams<{ classId: string }>()
  return <Navigate to={`/academics/class/${classId}?view=attendance`} replace />
}

/** School hub Classes tab links to `class/:id` relative to `/schools/:schoolId` — land on the class page. */
function SchoolClassRedirect() {
  const { classId } = useParams<{ classId: string }>()
  const [searchParams] = useSearchParams()
  const qs = searchParams.toString()
  const to = qs ? `/academics/class/${classId}?${qs}` : `/academics/class/${classId}`
  return <Navigate to={to} replace />
}

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { index: true, element: <Navigate to='login' replace /> },
          { path: 'login', element: LazyPage(LoginPage) },
          { path: 'forgot-password', element: LazyPage(ForgotPasswordPage) },
          { path: 'reset-password', element: LazyPage(ResetPasswordPage) },
          { path: 'accept-invitation', element: LazyPage(AcceptInvitationPage) },
          { path: 'register/parent', element: LazyPage(RegisterParentPage) },
          { path: 'verify-email', element: LazyPage(ParentEmailVerificationPage) },
          // Admin moved a parent account to a new email; the parent confirms here.
          { path: 'confirm-email-change', element: LazyPage(ConfirmEmailChangePage) },
          // An imported family's parent sets a password to join.
          { path: 'accept-family-invite', element: LazyPage(AcceptFamilyInvitePage) },
          { path: 'oauth/callback', element: LazyPage(OAuthCallbackPage) },
          // One-off registration invite link; works signed in or out.
          { path: 'registration-invite', element: LazyPage(RegistrationInviteLandingPage) },
        ],
      },
      {
        element: <AuthGuard />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              { index: true, element: LazyPage(DashboardPage) },
              { path: 'dashboard', element: LazyPage(DashboardPage) },
              // Role-scoped dashboard sub-routes (spec §6) — sysadmins may visit
              // any sub-route to preview; other roles are locked to their own.
              {
                element: <RoleGuard allowedRoles={['SYSTEM_ADMIN']} />,
                children: [{ path: 'dashboard/system', element: LazyPage(SystemDashboard) }],
              },
              {
                element: <RoleGuard allowedRoles={['SYSTEM_ADMIN', 'SCHOOL_ADMIN']} />,
                children: [{ path: 'dashboard/school', element: LazyPage(SchoolDashboard) }],
              },
              {
                element: <RoleGuard allowedRoles={['SYSTEM_ADMIN', 'TEACHER']} />,
                children: [
                  { path: 'dashboard/teacher', element: LazyPage(TeacherDashboard) },
                  // Legacy class landing — redirect to attendance view.
                  {
                    path: 'dashboard/teacher/class/:classId',
                    element: <TeacherClassRedirect />,
                  },
                ],
              },
              {
                element: <RoleGuard allowedRoles={['SYSTEM_ADMIN', 'PARENT']} />,
                children: [{ path: 'dashboard/parent', element: LazyPage(ParentDashboard) }],
              },
              {
                // POST /schools/ is SYSTEM_ADMIN only; school admins manage existing schools.
                element: <RoleGuard allowedRoles={['SYSTEM_ADMIN']} />,
                children: [{ path: 'schools/new', element: LazyPage(SchoolCreateWizardPage) }],
              },
              { path: 'profile', element: LazyPage(ProfilePage) },
              { path: 'profile/settings', element: LazyPage(ProfileEditPage) },
              {
                element: <RoleGuard allowedRoles={['SYSTEM_ADMIN', 'SCHOOL_ADMIN']} />,
                children: [
                  { path: 'user-management', element: LazyPage(UserManagementPage) },
                  { path: 'invitations', element: LazyPage(InvitationsListPage) },
                  { path: 'schools', element: LazyPage(SchoolsListPage) },
                  { path: 'classes', element: LazyPage(ClassesEntryPage) },
                  { path: 'schools/:id/setup', element: LazyPage(SchoolSetupWizardPage) },
                  {
                    path: 'schools/:id/academic-year/new',
                    element: LazyPage(AcademicYearWizardPage),
                  },
                  { path: 'schools/:id/rollover', element: LazyPage(RolloverWizardPage) },
                  { path: 'schools/:id', element: LazyPage(SchoolDetailPage) },
                  {
                    path: 'schools/:id/class/:classId',
                    element: <SchoolClassRedirect />,
                  },
                  {
                    path: 'schools/:id/report-cards',
                    element: LazyPage(SchoolReportCardsPage),
                  },
                  // Deprecated: grades / calendar / school-years are now tabs on /schools/:id.
                  // Redirect so deep links still land in a sensible place.
                  {
                    path: 'schools/:id/grades',
                    element: <SchoolHubTabRedirect tab='structure' />,
                  },
                  {
                    path: 'schools/:id/calendar',
                    element: <SchoolHubTabRedirect tab='calendar' />,
                  },
                  { path: 'school-years', element: <Navigate to='/schools' replace /> },
                  {
                    path: 'school-years/:schoolYearId',
                    element: <Navigate to='/schools' replace />,
                  },
                  {
                    path: 'registration-management',
                    element: LazyPage(RegistrationManagementDashboardPage),
                  },
                  {
                    path: 'registration-management/withdrawals',
                    element: LazyPage(WithdrawalRequestsPage),
                  },
                  // Registration list/admin moved into school hub Registration tab.
                  {
                    path: 'schools/:id/registration',
                    element: <RegistrationTabRedirect sub='forms' />,
                  },
                  {
                    path: 'schools/:id/registration/admin',
                    element: <RegistrationTabRedirect sub='submissions' />,
                  },
                  {
                    path: 'schools/:id/registration/admin/:registrationId',
                    element: LazyPage(AdminRegistrationDetailPage),
                  },
                  {
                    path: 'schools/:id/registration/new',
                    element: LazyPage(RegistrationFormCreatePage),
                  },
                  // Form detail passthrough killed — jump straight to edit.
                  {
                    path: 'schools/:id/registration/:formId',
                    element: <FormDetailToEditRedirect />,
                  },
                  {
                    path: 'schools/:id/registration/:formId/edit',
                    element: LazyPage(RegistrationFormEditPage),
                  },
                  {
                    path: 'schools/:id/registration/:formId/returning',
                    element: LazyPage(ReturningStudentsWizardPage),
                  },
                  {
                    path: 'schools/:id/returning/:campaignId',
                    element: LazyPage(ReturningStudentsTrackingPage),
                  },
                  { path: 'payments/feed', element: LazyPage(PaymentFeedPage) },
                  { path: 'payments/customers', element: LazyPage(CustomerLedgerPage) },
                  { path: 'payments/customers/:id', element: LazyPage(CustomerDetailPage) },
                  { path: 'payments/follow-up', element: LazyPage(FollowUpQueuePage) },
                  // Privacy & FERPA for school admins (the super admin may pick any school).
                  // Bare `/privacy` belongs to the parent area.
                  { path: 'privacy/school', element: LazyPage(SchoolPrivacyPage) },
                  {
                    path: 'privacy/school/students/:identityId',
                    element: LazyPage(SchoolStudentTimelinePage),
                  },
                  {
                    path: 'privacy/school/requests/:requestId',
                    element: LazyPage(RecordsRequestDetailPage),
                  },
                  {
                    path: 'privacy/school/disclosures/new',
                    element: LazyPage(RecordDisclosurePage),
                  },
                ],
              },
              {
                // Platform FERPA log review: `is_super_admin` only.
                element: <SuperAdminGuard />,
                children: [
                  { path: 'privacy/audit', element: LazyPage(PrivacyAuditPage) },
                  {
                    path: 'privacy/students/:identityId',
                    element: LazyPage(PlatformStudentTimelinePage),
                  },
                  { path: 'privacy/actors/:userId', element: LazyPage(ActorActivityPage) },
                ],
              },
              {
                element: <RoleGuard allowedRoles={['SYSTEM_ADMIN', 'SCHOOL_ADMIN', 'TEACHER']} />,
                children: [
                  // /academics is deprecated; classes are reached from school hub → class.
                  { path: 'academics', element: <Navigate to='/schools' replace /> },
                  { path: 'academics/class/:classId', element: LazyPage(ClassDetailPage) },
                ],
              },
              {
                element: <RoleGuard allowedRoles={['SYSTEM_ADMIN', 'SCHOOL_ADMIN']} />,
                children: [
                  { path: 'students', element: LazyPage(StudentsListPage) },
                  { path: 'documents/review', element: LazyPage(DocumentReviewQueuePage) },
                  { path: 'users', element: LazyPage(UsersListPage) },
                  { path: 'users/:id', element: LazyPage(UserDetailPage) },
                  { path: 'login-history', element: LazyPage(LoginHistoryPage) },
                ],
              },
              {
                element: (
                  <RoleGuard allowedRoles={['SYSTEM_ADMIN', 'SCHOOL_ADMIN', 'TEACHER', 'PARENT']} />
                ),
                children: [{ path: 'students/:id', element: LazyPage(StudentDetailPage) }],
              },
              {
                // App receipts and year-end statements: parents read their own family, staff
                // read families at their schools (the API enforces scope and returns 404).
                element: <RoleGuard allowedRoles={['SYSTEM_ADMIN', 'SCHOOL_ADMIN', 'PARENT']} />,
                children: [
                  { path: 'payments/receipts/:paymentId', element: LazyPage(PaymentReceiptPage) },
                  { path: 'payments/statements/:year', element: LazyPage(FamilyStatementPage) },
                ],
              },
              {
                element: <RoleGuard allowedRoles={['SYSTEM_ADMIN', 'SCHOOL_ADMIN', 'TEACHER']} />,
                children: [
                  {
                    path: 'report-cards/:reportCardId/print',
                    element: LazyPage(ReportCardPrintPage),
                  },
                ],
              },
              {
                element: <RoleGuard allowedRoles={['PARENT']} />,
                children: [
                  { path: 'children', element: <Navigate to='/dashboard/parent' replace /> },
                  { path: 'children/:identityId', element: LazyPage(ParentChildDetailPage) },
                  { path: 'children/:identityId/privacy', element: LazyPage(ParentPrivacyPage) },
                  { path: 'privacy', element: LazyPage(ParentPrivacyHubPage) },
                  {
                    path: 'children/:identityId/report-cards/:reportCardId',
                    element: LazyPage(ReportCardPrintPage),
                  },
                  { path: 'registrations', element: LazyPage(ParentRegistrationFormsPage) },
                  // Payments home (Stripe portal return and payment emails).
                  { path: 'payments', element: LazyPage(ParentPaymentsPage) },
                  // Legacy billing links from older emails and portal settings.
                  { path: 'registration/billing', element: <Navigate to='/payments' replace /> },
                  { path: 'account/billing', element: <Navigate to='/payments' replace /> },
                  {
                    path: 'registrations/plan/:batchId',
                    element: LazyPage(RegistrationPlanDetailPage),
                  },
                  {
                    path: 'registration/:formId/register',
                    element: LazyPage(ParentRegistrationPage),
                  },
                  { path: 'registration/payment/success', element: LazyPage(PaymentSuccessPage) },
                  { path: 'registration/payment/cancel', element: LazyPage(PaymentCancelPage) },
                  { path: 'returning/:campaignId', element: LazyPage(ParentReturningPage) },
                ],
              },
            ],
          },
        ],
      },
      { path: 'unauthorized', element: LazyPage(UnauthorizedPage) },
      { path: '*', element: LazyPage(NotFoundPage) },
    ],
  },
]
